import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDate, inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button, Card, Chip, Skeleton, Empty } from "../components/ui";
import {
  Users, PlusCircle, Shield, Calendar, TrendingUp, Crown,
  Building2, Star, ArrowRight, LogOut, Settings, ChevronRight, Mail,
  AlertCircle, Info, Lock, Check, X, ArrowLeft, ExternalLink
} from "lucide-react";

function getBadgeColor(planId) {
  switch (planId) {
    case "starter": return "success";
    case "league": return "default";
    case "premier": return "warning";
    default: return "default";
  }
}

function getBadgeLabel(planId) {
  switch (planId) {
    case "starter": return "Starter";
    case "league": return "League";
    case "premier": return "Premier";
    default: return "Free";
  }
}

export default function ClubSelectionPage() {
  const nav = useNavigate();
  const { user, clubs, refreshMe, logout, switchClub } = useAuth();
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(null);

  // Plan limits configuration
  const PLAN_LIMITS = {
    starter: 2,
    league: 5,
    premier: 10,
  };

  const PLAN_LABELS = {
    starter: "Starter (Free)",
    league: "League",
    premier: "Premier",
  };

  // Determine user's plan from clubs (use first club's subscription_badge or default to starter)
  const getUserPlan = () => {
    if (clubs.length > 0) {
      return clubs[0].subscription_badge || "starter";
    }
    return user?.subscription_plan || "starter";
  };

  const userPlan = getUserPlan();
  const maxClubs = PLAN_LIMITS[userPlan] || 2;
  const ownedClubs = clubs.filter(c => c.my_role === "owner").length;
  const canCreateClub = ownedClubs < maxClubs;

  useEffect(() => {
    if (user && clubs.length === 1 && user.active_club_id) {
      nav("/home", { replace: true });
    }
  }, [user, clubs, nav]);

  const handleSwitchClub = async (clubId) => {
    setSwitching(clubId);
    try {
      await switchClub(clubId);
      toast.success("Switched club");
      nav("/home", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSwitching(null);
    }
  };

  const handleCreateClub = () => {
    if (!canCreateClub) return;
    nav("/clubs/create", { replace: true });
  };

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  if (user === null) {
    return (
      <div className="app-shell grid place-items-center">
        <div className="text-center">
          <div className="h-16 w-16 rounded-3xl bg-brand grid place-items-center mx-auto mb-4">
            <Users size={28} strokeWidth={2} className="text-white" />
          </div>
          <div className="text-sm text-ink-muted">Loading…</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="app-shell flex flex-col min-h-screen bg-white">
      <div className="px-5 pt-8 pb-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold">Welcome back, {user.name?.split(" ")[0]} 👋</h1>
            <p className="text-ink-muted mt-1 text-[15px]">Select a club to continue</p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
            <LogOut size={18} /> Sign out
          </Button>
        </div>

        {/* Plan Limit Info */}
        <div className="mb-4 p-3 rounded-xl bg-brand-50 border border-brand-100">
          <div className="flex items-center gap-2 text-sm text-brand-700">
            <Info size={16} />
            <span className="font-medium">Current Plan: {PLAN_LABELS[userPlan]}</span>
            <span className="text-ink-muted">|</span>
            <span className="font-medium">{ownedClubs}/{maxClubs} clubs</span>
            {!canCreateClub && (
              <span className="text-amber-600 font-medium ml-2">Limit reached</span>
            )}
          </div>
        </div>

        {clubs.length === 0 ? (
          <div className="text-center py-12">
            <Empty
              title="No clubs yet"
              subtitle="Create your first cricket club or join one with an invite code"
              icon={Users}
            />
            <Button size="lg" onClick={handleCreateClub} className="mt-4 w-full sm:w-auto" disabled={!canCreateClub} data-testid="create-first-club-btn">
              <PlusCircle size={20} /> {canCreateClub ? "Create Club" : "Upgrade to create more clubs"}
            </Button>
            {!canCreateClub && (
              <p className="text-xs text-amber-600 mt-2 text-center">
                Free plan allows only {maxClubs} clubs. Upgrade to create more.
              </p>
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {clubs.map((c) => (
              <ClubCard
                key={c.id}
                club={c}
                isActive={c.id === user.active_club_id}
                onEnter={() => handleSwitchClub(c.id)}
                switching={switching === c.id}
              />
            ))}
            <CreateClubCard onClick={handleCreateClub} disabled={!canCreateClub} planLimit={maxClubs} currentCount={ownedClubs} planName={PLAN_LABELS[userPlan]} />
          </div>
        )}
      </div>

      <div className="border-t border-black/5 px-5 py-4">
        <p className="text-xs text-center text-ink-muted">
          Manage multiple cricket clubs under one account. Upgrade your plan for more clubs.
        </p>
        <Button variant="ghost" size="sm" className="w-full mt-3" onClick={() => nav("/invitations")} data-testid="club-selection-invitations-btn">
          <Mail size={14} /> Manage Invitations
        </Button>
      </div>
    </div>
  );
}

function ClubCard({ club, isActive, onEnter, switching }) {
  const myRole = club.my_role;
  const memberCount = club.member_count || 0;
  const badge = club.subscription_badge || "starter";
  const activeMatch = club.active_match;

  return (
    <Card className={`relative ${isActive ? "ring-2 ring-brand" : ""} h-full transition-all`}>
      <div className="flex items-start justify-between mb-4">
        <div className="h-12 w-12 rounded-2xl bg-brand-100 grid place-items-center">
          {club.logo ? (
            <img src={club.logo} alt={club.name} className="h-10 w-10 rounded-xl object-cover" />
          ) : (
            <Building2 size={22} className="text-brand" />
          )}
        </div>
        <Chip tone={getBadgeColor(badge)} className="text-xs">{getBadgeLabel(badge)}</Chip>
      </div>

      <h3 className="font-bold text-lg truncate mb-1">{club.name}</h3>
      <p className="text-sm text-ink-muted mb-3 truncate">{club.city}, {club.country}</p>

      <div className="flex items-center gap-3 text-xs text-ink-muted mb-3">
        <span className="flex items-center gap-1">
          <Users size={12} /> {memberCount} members
        </span>
        <span className="flex items-center gap-1">
          <Shield size={12} /> {myRole}
        </span>
      </div>

      {activeMatch && (
        <div className="bg-brand-50 rounded-xl p-3 mb-3 text-sm">
          <div className="flex items-center gap-1.5 text-brand mb-1">
            <Calendar size={12} /> Upcoming Match
          </div>
          <div className="font-medium truncate">{activeMatch.opponent}</div>
          <div className="text-xs text-ink-muted">{fmtDate(activeMatch.date)} · {activeMatch.ground}</div>
        </div>
      )}

      <Button
        className="w-full"
        variant={isActive ? "dark" : "outline"}
        size="lg"
        onClick={onEnter}
        disabled={switching}
        data-testid={`enter-club-${club.id}`}
      >
        {switching ? (
          <>
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            Switching…
          </>
        ) : isActive ? (
          <>
            <Check size={18} /> Current Club
          </>
        ) : (
          <>
            <ArrowRight size={18} /> Enter Club
          </>
        )}
      </Button>
    </Card>
  );
}

function CreateClubCard({ onClick, disabled, planLimit, currentCount, planName }) {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  if (disabled) {
    return (
      <Card className="border-2 border-dashed border-brand-200 hover:border-brand-300 bg-brand-50 transition-colors h-full flex flex-col justify-center items-center text-center p-8" onClick={() => setShowUpgradeModal(true)}>
        <div className="h-16 w-16 rounded-2xl bg-brand-100 grid place-items-center mb-4">
          <Lock size={32} className="text-brand" />
        </div>
        <h3 className="font-bold text-lg text-ink mb-1">Club Limit Reached</h3>
        <p className="text-sm text-ink-muted mb-3">Free plan allows only {planLimit} club{planLimit > 1 ? "s" : ""}</p>
        <Button variant="outline" size="lg" onClick={() => setShowUpgradeModal(true)} data-testid="upgrade-plan-btn">
          <ArrowRight size={18} /> View Plans
        </Button>
        <p className="text-xs text-brand mt-2 text-center">Upgrade to {planName} for more clubs</p>
        <UpgradeModal
          open={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          planLimit={planLimit}
          currentCount={currentCount}
          planName={planName}
        />
      </Card>
    );
  }

  return (
    <Card className="border-2 border-dashed border-brand-200 hover:border-brand-400 transition-colors cursor-pointer h-full flex flex-col justify-center items-center text-center p-8" onClick={onClick}>
      <div className="h-16 w-16 rounded-2xl bg-brand-100 grid place-items-center mb-4">
        <PlusCircle size={32} className="text-brand" />
      </div>
      <h3 className="font-bold text-lg text-ink mb-1">Create New Club</h3>
      <p className="text-sm text-ink-muted mb-4">Start a new cricket community</p>
      <Button variant="primary" size="lg" onClick={(e) => { e.stopPropagation(); onClick(); }} data-testid="create-club-card-btn">
        <PlusCircle size={20} /> Create Club
      </Button>
    </Card>
  );
}

function UpgradeModal({ open, onClose, planLimit, currentCount, planName }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50" data-testid="upgrade-modal-overlay">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden
      />
      <div className="absolute inset-x-0 bottom-0 max-w-[480px] mx-auto bg-white rounded-t-3xl p-5 pb-8 max-h-[92vh] overflow-y-auto animate-in slide-in-from-bottom-2">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-ink">Club Limit Reached</h3>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-full bg-black/5 hover:bg-black/10"
            data-testid="upgrade-modal-close"
            aria-label="Close"
          >
            <X size={24} className="text-ink-muted" />
          </button>
        </div>
        <div className="text-center mb-6">
          <div className="h-16 w-16 rounded-2xl bg-brand-100 grid place-items-center mx-auto mb-4">
            <Lock size={32} className="text-brand" />
          </div>
          <h3 className="font-bold text-lg text-ink mb-1">Club Limit Reached</h3>
          <p className="text-sm text-ink-muted mb-4">Free plan allows only {planLimit} club{planLimit > 1 ? "s" : ""}</p>
          <p className="text-sm text-ink-muted">You have {currentCount} club{currentCount !== 1 ? "s" : ""} out of {planLimit} allowed.</p>
        </div>
        <div className="space-y-3">
          <Button variant="primary" size="lg" className="w-full" onClick={() => { onClose(); window.open("/pricing", "_blank"); }} data-testid="upgrade-modal-view-plans">
            <ArrowRight size={20} /> View All Plans
          </Button>
          <Button variant="outline" size="lg" className="w-full" onClick={onClose} data-testid="upgrade-modal-cancel">
            Not Now
          </Button>
        </div>
      </div>
    </div>
  );
}